export interface Article {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  content: string;
  imageUrl: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  featured: boolean;
}

export const articles: Article[] = [
  {
    slug: "caomei-shipin-secai-jichu",
    title: "草莓视频色彩基础：从色相环理解草莓的红色魅力",
    category: "caomei-shipin-se",
    excerpt: "草莓的红色并非单一的红色，它包含了从深红到浅粉的丰富层次。理解色相环中红色的位置与变化规律，是做好草莓视频色彩校正的第一步。本文将带你从色彩理论出发，深入理解草莓视频中的色彩构成。",
    content: `<h2>草莓红色的色彩构成</h2>
<p>草莓的红色是一种极具感染力的色彩，它能够在画面中第一时间吸引观众的目光。但很多人不知道的是，草莓的红色实际上包含了极为丰富的色彩层次——从成熟果实表面的深红色，到果肉切面的粉红色，再到光照下的高光区域的浅粉色，每一种红都有其独特的色相值。</p>
<p>在色相环（Hue Circle）上，红色的色相角度大约在0°到15°之间。草莓的红色通常在5°到10°的范围内，这是一个偏暖的红色区域。理解这一点至关重要，因为它决定了你在后期调色时应该在哪个方向上调整色相。</p>
<h2>饱和度与明度的关系</h2>
<p>草莓视频色彩的魅力不仅仅来自色相，饱和度（Saturation）和明度（Luminance）同样起着关键作用。一颗新鲜草莓的饱和度通常在70%-90%之间，明度则在40%-60%之间。如果饱和度过高，草莓会显得不自然甚至刺眼；如果明度过低，草莓则会显得暗淡无光。</p>
<p>在实际操作中，建议使用矢量示波器（Vectorscope）来监控草莓红色的饱和度。理想的草莓红应该在矢量示波器的红色区域内，但不要触及边界，保留一定的余量以避免色彩溢出。</p>
<h2>色彩搭配的艺术</h2>
<p>草莓视频中，草莓通常不是孤立出现的。绿叶、白色奶油、深色巧克力、浅色背景等元素与草莓构成色彩对比。绿色的叶子和红色的草莓形成了经典的互补色关系（色相环上相差180°），这种对比能让两种颜色都更加鲜艳。在调色时，适当增强绿色的饱和度可以间接提升草莓红色的视觉冲击力。</p>
<h2>常见色彩问题与解决方案</h2>
<p><strong>问题一：草莓发黄发暗</strong>——这通常是白平衡设置不当导致的。拍摄时如果使用了偏暖的灯光但白平衡设置为日光，草莓就会偏黄。解决方案是在后期调色时将色温向冷色调微调，或者在拍摄时就使用正确的白平衡预设。</p>
<p><strong>问题二：草莓红色溢出</strong>——这是饱和度过度调整的典型症状。红色溢出表现为草莓表面细节丢失，变成一片纯红色块。解决方案是使用色彩键（Qualifier）选择红色区域，适当降低饱和度并调整亮度曲线。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Vibrant%20fresh%20ripe%20strawberries%20arranged%20on%20a%20clean%20minimalist%20white%20surface%20with%20soft%20natural%20daylight%2C%20color%20wheel%20artistic%20overlay%2C%20professional%20food%20photography%20style%2C%20bright%20and%20fresh%20aesthetic%2C%20shallow%20depth%20of%20field%2C%20rich%20red%20tones%20with%20organic%20texture&width=800&height=500&seq=article-color-basics&orientation=landscape",
    author: "李明远",
    date: "2026-07-15",
    readTime: "8分钟",
    tags: ["色彩理论", "色相环", "草莓视频", "饱和度"],
    featured: true,
  },
  {
    slug: "caomei-shipin-baipingheng-jiaozheng",
    title: "草莓视频白平衡校正完全指南",
    category: "caomei-shipin-tiaose",
    excerpt: "白平衡是草莓视频调色中最基础也最关键的一步。错误的白平衡会让草莓失去诱人的色泽，变得发黄或发蓝。本文将详细讲解如何在拍摄和后期中正确设置白平衡，让草莓呈现出最自然、最诱人的红色。",
    content: `<h2>为什么白平衡对草莓视频如此重要</h2>
<p>草莓的红色是一种非常敏感的色彩——即使是轻微的色温偏差，也会让草莓的红色发生明显变化。偏暖的色温会让草莓发黄，偏冷的色温则会让草莓发紫发蓝，这两种情况都会让草莓看起来不够新鲜和诱人。</p>
<p>白平衡的核心作用就是告诉相机"什么是白色"，从而确保所有色彩都能被准确还原。对于草莓视频来说，准确的白平衡是让草莓红色"正"的基础。</p>
<h2>拍摄时的白平衡设置</h2>
<p><strong>使用灰卡校准</strong>：在正式拍摄草莓之前，将18%灰卡放在草莓所在的位置，让相机对灰卡进行自定义白平衡。这是最准确的方法，尤其适用于棚拍场景。</p>
<p><strong>选择正确的预设</strong>：如果无法使用灰卡，请根据光源类型选择对应的白平衡预设。自然光拍摄选择"日光"（约5600K），钨丝灯下选择"钨丝灯"（约3200K），LED灯则需要根据灯光的实际色温来设置。</p>
<p><strong>统一光源色温</strong>：混合光源是白平衡的噩梦。确保拍摄草莓时使用的所有灯光具有相同的色温，避免画面中出现冷暖混搭的尴尬情况。</p>
<h2>后期白平衡校正技巧</h2>
<p>Raw格式的视频文件（如ProRes RAW或BRAW）可以在后期无损调整白平衡。在达芬奇（DaVinci Resolve）中，使用色温滑块将草莓的红色调整到你认为最自然的程度。一个实用的参考标准是：草莓的红色应该与你在自然光下看到的草莓颜色尽可能一致。</p>
<p>如果是H.264或H.265等压缩格式的视频，白平衡调整会有所限制，但仍可以通过色轮工具进行补救。建议在Pr或FCPX中使用"自动白平衡"作为起点，然后手动微调。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Fresh%20strawberries%20being%20photographed%20in%20a%20professional%20studio%20setup%20with%20color%20calibration%20chart%20and%20soft%20diffused%20lighting%2C%20clean%20white%20background%2C%20professional%20food%20photography%20equipment%20visible%2C%20warm%20natural%20tones%2C%20editorial%20commercial%20style&width=800&height=500&seq=article-whitebalance&orientation=landscape",
    author: "李明远",
    date: "2026-07-10",
    readTime: "10分钟",
    tags: ["白平衡", "色温", "灰卡校准", "草莓调色"],
    featured: true,
  },
  {
    slug: "caomei-paishe-dengguang-buzhi",
    title: "草莓拍摄灯光布置：打造诱人光泽感的秘诀",
    category: "caomei-paishe-jiqiao",
    excerpt: "灯光是草莓视频拍摄的灵魂。恰当的灯光布置能够凸显草莓的纹理、光泽和鲜艳色彩。本文分享专业食品摄影师都在用的灯光方案，从单灯到多灯布置，手把手教你打出令人垂涎的草莓光泽。",
    content: `<h2>理解草莓的光质需求</h2>
<p>草莓表面具有天然的蜡质层，在适当的光照下会产生迷人的光泽。这种光泽感是草莓视频"诱人"的核心要素之一。为了捕捉这一效果，我们需要使用柔和的散射光作为主光源，同时配合精细的高光控制。</p>
<p>草莓的质地在不同部位是不同的——果肉部分是半透明的，种子部分是硬质的，叶片部分则具有哑光质感。这意味着你需要用不同的光质来处理不同的部位，而这正是灯光布置的艺术所在。</p>
<h2>经典三灯布置方案</h2>
<p><strong>主光（Key Light）</strong>：使用大型柔光箱（建议90cm以上）从草莓的左前方45°照射。这是塑造草莓立体感的关键光源，功率占比约60%。柔光箱越大，光线越柔和，草莓表面的过渡越自然。</p>
<p><strong>补光（Fill Light）</strong>：在草莓右侧放置反光板或使用较小功率的灯光进行补光，功率占比约25%。补光的作用是减少阴影区域的浓度，保留草莓暗部的细节。</p>
<p><strong>顶光/背光（Rim Light）</strong>：从草莓的上方或后方打一盏小功率灯光，功率占比约15%。这盏灯用于在草莓的边缘产生高光轮廓，让草莓从背景中"跳"出来。</p>
<h2>水珠效果的打光技巧</h2>
<p>在草莓上喷洒细小水珠是增强新鲜感的常用手法。要让水珠呈现晶莹剔透的效果，需要使用点光源（如裸灯或小反光罩）从侧面照射，这样每个小水珠都会反射出明亮的高光点。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Professional%20studio%20lighting%20setup%20for%20food%20photography%20with%20fresh%20strawberries%20on%20a%20marble%20surface%2C%20softbox%20lights%20and%20reflectors%20visible%2C%20dramatic%20yet%20natural%20look%2C%20glossy%20strawberry%20texture%20with%20water%20droplets%2C%20commercial%20food%20photography%20style&width=800&height=500&seq=article-lighting&orientation=landscape",
    author: "王思涵",
    date: "2026-06-28",
    readTime: "12分钟",
    tags: ["灯光布置", "柔光箱", "草莓拍摄", "三灯法"],
    featured: true,
  },
  {
    slug: "dafenqi-jichu-diaose-liucheng",
    title: "达芬奇调色基础流程：草莓视频专用工作流",
    category: "caomei-shipin-tiaose",
    excerpt: "达芬奇（DaVinci Resolve）是专业视频调色的行业标准。本文为草莓视频创作者量身定制了一套完整的达芬奇调色工作流，从项目设置到最终输出，每一步都有详细的操作指引。",
    content: `<h2>项目设置与色彩管理</h2>
<p>在开始调色之前，正确的项目设置至关重要。打开达芬奇后，首先在项目设置中确认色彩科学（Color Science）选择为DaVinci YRGB Color Managed，这样可以让达芬奇自动管理色彩空间转换，减少手动操作中的失误。</p>
<p>时间线色彩空间建议设置为Rec.709（用于网络视频）或Rec.2020（用于HDR视频）。如果你的素材是Log格式拍摄的，需要确保输入色彩空间设置与拍摄时一致，例如S-Log3、C-Log2或V-Log。</p>
<h2>节点树结构设计</h2>
<p>一个高效的节点树结构是专业调色的标志。对于草莓视频，推荐以下节点排列：</p>
<p><strong>节点1 - 降噪</strong>：使用时域降噪和空域降噪清理画面噪点，为后续调色打基础。</p>
<p><strong>节点2 - 色彩空间转换</strong>：将Log素材转换为Rec.709色彩空间。</p>
<p><strong>节点3 - 一级调色</strong>：调整对比度、曝光和色彩平衡。使用Lift/Gamma/Gain色轮进行粗调。</p>
<p><strong>节点4 - 二级调色</strong>：使用限定器（Qualifier）单独选择草莓的红色区域进行精细调整。</p>
<p><strong>节点5 - 风格化</strong>：添加Look或LUT，赋予视频独特的视觉风格。</p>
<h2>草莓红色专项调色技巧</h2>
<p>在二级调色节点中，使用HSL限定器选中草莓的红色范围。然后将色相微调至5°-8°的范围，这是最令人愉悦的草莓红色调。饱和度适度提升10%-15%，但要确保不出现色彩溢出。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Professional%20color%20grading%20workstation%20with%20DaVinci%20Resolve%20interface%20on%20monitor%20displaying%20strawberry%20video%20footage%20with%20color%20wheels%20and%20scopes%2C%20dark%20editing%20suite%20environment%2C%20cinematic%20lighting%2C%20professional%20post-production%20setup&width=800&height=500&seq=article-davinci&orientation=landscape",
    author: "李明远",
    date: "2026-06-20",
    readTime: "15分钟",
    tags: ["达芬奇", "调色流程", "节点树", "色彩空间"],
    featured: true,
  },
  {
    slug: "caomei-secai-baohedu-kongzhi",
    title: "草莓色彩饱和度控制：如何避免红色溢出与失真",
    category: "caomei-shipin-se",
    excerpt: "饱和度是草莓视频调色中最容易「翻车」的参数。过高的饱和度导致红色溢出，过低的饱和度又让草莓显得暗淡无光。本文将详细讲解饱和度的精准控制技巧，让你的草莓红得恰到好处。",
    content: `<h2>饱和度的黄金区间</h2>
<p>草莓视频的饱和度调整需要遵循"适中偏上"的原则。经过对大量优质草莓视频素材的分析，我们总结出草莓红色的理想饱和度范围在矢量示波器上为65%-82%。这个区间的草莓红色鲜艳饱满但不失真，能够同时满足大众审美和专业标准。</p>
<p>值得注意的是，不同品种的草莓也有不同的饱和度特性。章姬草莓（奶油草莓）的红色饱和度偏低，红颜草莓的饱和度偏高，在调色时需要根据实际品种进行差异化处理。</p>
<h2>使用矢量示波器监控饱和度</h2>
<p>矢量示波器（Vectorscope）是监控饱和度的最佳工具。打开矢量示波器后，你会看到画面中的色彩以点的形式分布在圆形示波器上。草莓的红色应该分布在示波器红色区域（大约11点钟方向）的内部，点越远离中心，表示饱和度越高。</p>
<p>一个实用的技巧是：在示波器上打开肤色指示线（Skin Tone Line），草莓的最佳红色应该在这条线的右侧约10°-15°的位置。</p>
<h2>红色溢出的预警信号</h2>
<p>红色溢出（Red Clipping）是指红色通道的信号超过了显示设备或编码格式能够容纳的上限。在波形示波器（Waveform）上，如果红色通道的波形触顶（达到100 IRE或以上），就意味着出现了红色溢出。</p>
<p>红色溢出会导致草莓表面细节完全丢失，呈现为一片不均匀的红色色块，这是调色中必须避免的情况。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Close-up%20macro%20photography%20of%20fresh%20ripe%20strawberries%20with%20perfect%20color%20saturation%2C%20vibrant%20red%20tones%20with%20visible%20texture%20and%20seeds%2C%20studio%20lighting%20with%20soft%20highlights%2C%20professional%20food%20photography%2C%20extremely%20detailed%20surface%2C%20clean%20composition&width=800&height=500&seq=article-saturation&orientation=landscape",
    author: "王思涵",
    date: "2026-06-15",
    readTime: "9分钟",
    tags: ["饱和度", "矢量示波器", "红色溢出", "色彩控制"],
    featured: false,
  },
  {
    slug: "shipin-secai-jiaozheng-lut",
    title: "视频色彩校正必备LUT推荐：草莓视频专用预设合集",
    category: "shipin-secai-jiaozheng",
    excerpt: "LUT（查找表）是视频调色中的「快捷键」，正确的LUT能大幅提升工作效率。本文整理了最适合草莓视频色彩校正的LUT合集，涵盖从Log转换到风格化呈现的各个环节。",
    content: `<h2>什么是LUT？为什么草莓视频需要它</h2>
<p>LUT（Look-Up Table，查找表）本质上是一个色彩映射表，它将输入的色彩值映射为输出的色彩值。在视频调色中，LUT能够快速地实现复杂的色彩转换，比如将Log素材转换为Rec.709标准色彩，或者赋予画面特定的风格化外观。</p>
<p>对于草莓视频创作者来说，LUT的价值在于能够快速建立一致的色彩风格。当你在一个项目中拍摄了大量草莓素材，使用统一的LUT可以确保所有片段的色彩基调一致。</p>
<h2>推荐LUT分类</h2>
<p><strong>技术转换LUT</strong>：这类LUT用于色彩空间转换，不涉及风格化。推荐使用相机厂商官方提供的Log-to-Rec709 LUT，如索尼的S-Log3 LUT、佳能的C-Log2 LUT等。</p>
<p><strong>食品增强LUT</strong>：这类LUT专门针对食品视频优化，通常会增强红色和黄色的饱和度，同时轻微提升对比度。推荐使用FilmConvert的Food预设系列。</p>
<p><strong>风格化LUT</strong>：如果你想让草莓视频呈现电影感、复古感或清新感，可以使用风格化LUT。推荐使用LUTs by Chris的Cinematic Food系列。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Video%20color%20grading%20before%20and%20after%20comparison%20of%20strawberry%20footage%2C%20split%20screen%20showing%20flat%20log%20footage%20versus%20graded%20vibrant%20result%2C%20digital%20editing%20suite%20monitor%2C%20professional%20post-production%2C%20clean%20and%20modern%20workspace&width=800&height=500&seq=article-lut&orientation=landscape",
    author: "李明远",
    date: "2026-06-05",
    readTime: "11分钟",
    tags: ["LUT", "色彩转换", "预设", "工作效率"],
    featured: false,
  },
  {
    slug: "caomei-sewen-nuanleng-tiaozheng",
    title: "草莓色温深度解析：暖调与冷调的场景选择策略",
    category: "caomei-sewen-shezhi",
    excerpt: "色温决定了草莓视频的整体氛围——暖调让人感觉温馨甜美，冷调则显得清爽高级。本文将通过多个实战案例，教你如何针对不同场景和平台选择最合适的色温方案。",
    content: `<h2>色温的视觉心理效应</h2>
<p>色温不仅仅是技术参数，它直接决定了观众对草莓视频的情感反应。暖色调（低色温，约3000K-4500K）的草莓视频会给人一种温暖、甜美、家的感觉，非常适合美食Vlog和家庭烘焙类的内容。</p>
<p>冷色调（高色温，约5500K-7500K）的草莓视频则呈现出清爽、高级、专业的视觉效果，更适合商业广告和高端餐饮推广。了解色温的视觉心理效应，能够帮助你根据内容定位做出正确的选择。</p>
<h2>不同平台的色温策略</h2>
<p><strong>抖音/小红书</strong>：推荐偏暖色调（4500K-5000K），更符合这些平台的审美趋势，暖色调的视频在移动端小屏幕上更抓眼球。</p>
<p><strong>B站/YouTube</strong>：推荐中性偏暖（5000K-5500K），保留专业感的同时不失亲和力，适合长视频内容。</p>
<p><strong>商业广告</strong>：推荐中性偏冷（5500K-6000K），突出产品的高级感和品质感。</p>
<h2>混合色温场景的处理</h2>
<p>在实际拍摄中，经常会遇到混合色温的场景——比如窗边自然光（5600K）配合室内暖光灯（3200K）。这种场景下，建议优先保证草莓主体的色温准确，次要区域的色温偏差可以适当容忍。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Fresh%20strawberries%20photographed%20with%20warm%20golden%20sunset%20light%20versus%20cool%20morning%20light%20comparison%2C%20two%20different%20color%20temperature%20moods%2C%20professional%20food%20photography%2C%20soft%20natural%20lighting%2C%20artistic%20composition&width=800&height=500&seq=article-colortemp&orientation=landscape",
    author: "王思涵",
    date: "2026-05-28",
    readTime: "8分钟",
    tags: ["色温", "暖调", "冷调", "场景选择"],
    featured: false,
  },
  {
    slug: "shipin-meishi-tiaose-tongyong-faze",
    title: "食品视频调色通用法则：从草莓到各类美食的色彩管理",
    category: "shipin-shipin-tiaose",
    excerpt: "草莓视频调色的许多技巧同样适用于其他食品视频。本文将分享食品视频调色的通用法则，帮助你建立一个可复用的色彩管理方法，无论拍摄什么美食都能得心应手。",
    content: `<h2>食品视频调色的核心原则</h2>
<p>所有食品视频调色的终极目标都是让食物看起来"好吃"。这是一个简单但不容易达成的目标，因为它涉及到色彩、光影、质感等多个维度的综合表现。</p>
<p>食品调色有三个黄金法则：<strong>第一，让食物比现实中稍微鲜艳一点</strong>——这不是欺骗，而是弥补摄像设备在色彩捕捉上的局限；<strong>第二，确保食物的本色不被改变</strong>——红色的草莓不能变成橙色，绿色的蔬菜不能变成黄色；<strong>第三，营造食欲感</strong>——适当提升对比度和暖色调能够增强食物的美味感。</p>
<h2>各类食品的色彩特性</h2>
<p><strong>红色系食品</strong>（草莓、番茄、红肉）：重点关注红色通道的饱和度和明度，避免红色溢出。</p>
<p><strong>绿色系食品</strong>（蔬菜、抹茶）：绿色容易偏黄或偏蓝，需要精细调整绿色通道的色相角度。</p>
<p><strong>黄色系食品</strong>（面包、芝士、蛋黄）：黄色是食欲感最强的颜色，适当提升暖色调和饱和度可以显著增强美感。</p>
<p><strong>白色系食品</strong>（奶油、冰淇淋、米饭）：白色食品最难调色，需要确保白平衡绝对准确，避免偏色。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20flat%20lay%20of%20various%20fresh%20fruits%20including%20strawberries%20blueberries%20and%20citrus%20on%20a%20light%20wooden%20table%2C%20professional%20food%20photography%2C%20bright%20natural%20lighting%2C%20vibrant%20colors%2C%20clean%20and%20fresh%20composition%2C%20overhead%20shot&width=800&height=500&seq=article-food-general&orientation=landscape",
    author: "李明远",
    date: "2026-05-20",
    readTime: "14分钟",
    tags: ["食品调色", "通用法则", "色彩管理", "美食视频"],
    featured: true,
  },
  {
    slug: "caomei-raw-houqi-gongzuoliu",
    title: "草莓摄影后期全流程：从RAW文件到精美成片",
    category: "caomei-sheying-houqi",
    excerpt: "RAW格式保留了草莓照片最丰富的色彩信息，但也需要专业的后期处理才能发挥全部潜力。本文完整拆解草莓摄影后期处理的标准工作流，涵盖Lightroom和Photoshop两大工具。",
    content: `<h2>RAW文件处理的基础准备</h2>
<p>RAW格式是草莓摄影后期的最佳起点。与JPEG不同，RAW文件保留了传感器捕捉到的所有原始数据，包括14位的色彩深度和完整的动态范围。这意味着你在后期调整白平衡、曝光和色彩时有更大的操作空间，而不会损失画质。</p>
<p>在开始处理之前，确保你的显示器已经过校准（建议使用Spyder或i1 Display等校色仪），这样才能确保你看到的颜色是准确的。</p>
<h2>Lightroom基础调整流程</h2>
<p><strong>第一步：镜头校正</strong>——启用镜头配置文件校正，消除畸变和暗角。</p>
<p><strong>第二步：白平衡校准</strong>——使用白平衡选择器点击画面中应该为中性灰色的区域，或者手动调整色温和色调滑块。</p>
<p><strong>第三步：曝光与对比度</strong>——调整曝光度，确保草莓的高光区域不过曝。适度提升对比度以增强立体感。</p>
<p><strong>第四步：HSL精细调整</strong>——这是草莓后期的关键步骤。在HSL面板中，将红色的色相微调至+5到+10，饱和度提升+10到+15，明度微调-5到0。</p>
<h2>Photoshop精修技巧</h2>
<p>在Lightroom完成基础调整后，将照片导入Photoshop进行精细修图。使用频率分离技术处理草莓表面的瑕疵，使用加深减淡工具增强光影立体感。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Professional%20photo%20editing%20workspace%20with%20Lightroom%20and%20Photoshop%20interface%20on%20dual%20monitors%20showing%20strawberry%20photo%20editing%20process%2C%20before%20and%20after%20comparison%2C%20creative%20workspace%20with%20warm%20ambient%20lighting%2C%20modern%20desk%20setup&width=800&height=500&seq=article-raw-post&orientation=landscape",
    author: "王思涵",
    date: "2026-05-12",
    readTime: "12分钟",
    tags: ["RAW处理", "Lightroom", "Photoshop", "后期流程"],
    featured: false,
  },
  {
    slug: "caomei-paishe-jingtou-xuanze",
    title: "草莓拍摄镜头选择指南：从微距到广角的最佳搭配",
    category: "caomei-paishe-jiqiao",
    excerpt: "不同焦段的镜头会给草莓视频带来截然不同的视觉效果。微距镜头展现细节纹理，标准镜头还原真实比例，长焦镜头营造压缩感。本文帮你找到最适合草莓拍摄的镜头组合。",
    content: `<h2>微距镜头：草莓细节的终极呈现</h2>
<p>微距镜头（Macro Lens）是草莓拍摄中最常用的镜头类型。90mm或100mm的微距镜头能够以1:1的放大倍率捕捉草莓表面的种子、纹理和水珠，呈现肉眼难以察觉的细节之美。</p>
<p>推荐使用f/4-f/8的光圈范围——光圈太大会导致景深过浅，草莓只有极小部分清晰；光圈太小则会产生衍射效应，降低整体锐度。在f/5.6左右通常能找到最佳平衡点。</p>
<h2>标准镜头：真实自然的表现力</h2>
<p>50mm标准镜头的视角与人眼最为接近，拍出的草莓视频最自然、最真实。适合用于展示草莓的整体形态和与环境的互动关系。使用f/2.8的大光圈可以营造柔美的背景虚化，让草莓主体更加突出。</p>
<h2>广角与长焦的创意应用</h2>
<p>广角镜头（24mm-35mm）适合拍摄草莓园的大场景或厨房操作台的全景。长焦镜头（135mm-200mm）则能够压缩空间感，让成堆的草莓看起来更加密集丰富。了解每种镜头的特性，才能在不同拍摄场景中做出最佳选择。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Collection%20of%20professional%20camera%20lenses%20including%20macro%20lens%20standard%20lens%20and%20telephoto%20lens%20arranged%20on%20a%20wooden%20table%20with%20fresh%20strawberries%20nearby%2C%20studio%20photography%20equipment%2C%20clean%20professional%20aesthetic%2C%20natural%20lighting&width=800&height=500&seq=article-lens-guide&orientation=landscape",
    author: "王思涵",
    date: "2026-05-05",
    readTime: "10分钟",
    tags: ["镜头选择", "微距", "焦段", "拍摄设备"],
    featured: false,
  },
  {
    slug: "shipin-secai-xue-xiaobai-ru-men",
    title: "视频色彩学小白入门：30分钟搞懂调色的底层逻辑",
    category: "shipin-secai-jiaozheng",
    excerpt: "调色不是玄学，而是一门有规律可循的科学。本文用通俗易懂的语言，为零基础的小白讲解视频色彩学的基本概念和调色的底层逻辑，让你在30分钟内建立起完整的色彩知识框架。",
    content: `<h2>色彩的三要素</h2>
<p>理解色彩，需要从三个基本要素出发：<strong>色相（Hue）</strong>、<strong>饱和度（Saturation）</strong>和<strong>明度（Luminance）</strong>。这三个要素构成了所有色彩调整的基础框架。</p>
<p>色相就是"什么颜色"——红、橙、黄、绿、青、蓝、紫。饱和度就是"颜色有多浓"——从灰蒙蒙到鲜艳欲滴。明度就是"颜色有多亮"——从接近黑色到接近白色。任何调色操作，本质上都是在这三个维度上的调整。</p>
<h2>色轮与色彩关系</h2>
<p>色轮（Color Wheel）是理解色彩关系的最直观工具。在色轮上，相邻的颜色（如红和橙）是类似色，它们搭配在一起和谐自然；相对的颜色（如红和绿）是互补色，它们搭配在一起对比强烈、互相增强。</p>
<p>对于草莓视频来说，利用红色和绿色的互补关系是一个非常实用的技巧——适当增强画面中绿叶的饱和度，可以让草莓的红色看起来更加鲜艳。</p>
<h2>调色的工作流思维</h2>
<p>调色不是随机尝试，而是有条理的工作流程。遵循"一级调色→二级调色→风格化"的三步流程，可以让你告别"凭感觉乱调"的困境。一级调色解决曝光和色彩平衡问题，二级调色针对特定区域精细调整，风格化则赋予视频独特的视觉个性。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Simple%20educational%20color%20wheel%20diagram%20with%20fresh%20strawberries%20placed%20on%20a%20clean%20white%20desk%2C%20minimalist%20design%2C%20bright%20and%20inviting%20atmosphere%2C%20creative%20learning%20environment%2C%20natural%20daylight%20from%20window&width=800&height=500&seq=article-color-theory&orientation=landscape",
    author: "李明远",
    date: "2026-04-28",
    readTime: "6分钟",
    tags: ["色彩学", "入门教程", "色轮", "基础知识"],
    featured: false,
  },
  {
    slug: "caomei-shipin-chuqi-yusuan-fangan",
    title: "草莓视频拍摄设备推荐：从入门到专业的三套预算方案",
    category: "caomei-paishe-jiqiao",
    excerpt: "拍好草莓视频不一定需要天价设备。本文针对不同预算水平，推荐了三套完整的拍摄设备方案——从手机+自然光的入门组合，到专业机身+电影镜头的顶配方案，总有一套适合你。",
    content: `<h2>入门方案（预算：0-3000元）</h2>
<p>谁说好设备才能拍好草莓视频？一部近两年发布的旗舰手机（如iPhone 15 Pro或小米14 Ultra），加上自然光或一盏LED补光灯，就能拍出令人满意的草莓视频。关键在于充分利用手机的手动模式或第三方App（如Filmic Pro），锁定白平衡和曝光，避免自动模式的色彩波动。</p>
<p>推荐配件：一盏小型LED补光灯（约200元）、一个桌面三脚架（约100元）、一张白色PVC背景板（约50元）。</p>
<h2>进阶方案（预算：3000-15000元）</h2>
<p>这个预算区间是性价比最高的选择。推荐使用索尼ZV-E10或佳能EOS R50机身，搭配适马56mm f/1.4或佳能RF 50mm f/1.8镜头。这些设备能够拍摄4K 10-bit视频，为后期调色提供充足的色彩信息。</p>
<p>灯光方面，推荐两盏Godox SL60W常亮灯（各约600元）配合柔光箱，加上一块5合1反光板。</p>
<h2>专业方案（预算：15000元以上）</h2>
<p>如果你追求极致的画质和色彩表现，推荐索尼A7S III或佳能EOS R6 Mark II机身，搭配索尼90mm f/2.8 Macro或佳能RF 100mm f/2.8L Macro镜头。</p>`,
    imageUrl: "https://readdy.ai/api/search-image?query=Three%20tiers%20of%20video%20production%20equipment%20from%20smartphone%20to%20professional%20cinema%20camera%20arranged%20on%20a%20clean%20surface%20with%20strawberries%2C%20progressive%20gear%20comparison%2C%20minimalist%20product%20photography%2C%20bright%20studio%20lighting&width=800&height=500&seq=article-equipment&orientation=landscape",
    author: "王思涵",
    date: "2026-04-20",
    readTime: "13分钟",
    tags: ["设备推荐", "预算方案", "相机选择", "拍摄器材"],
    featured: false,
  },
];